import { Pose, Joints, LandmarkIndex, NUM_LANDMARKS } from './pose'

// Type definitions
export enum RepStyle {
  Single = 0,
  Dual = 1,
  Alternate = 2
}

export interface ExerciseState {
  index: number;
  score: number;
  valid: boolean;
}

export interface JointWeights {
  [key: string]: number;
}

export class ExerciseDefinition {
  public name: string
  public keyposes: Pose[]
  public repStyle: RepStyle
  public weights: JointWeights
  public requiredLandmarks: Set<LandmarkIndex>

  constructor (name: string, keyposes: Pose[], repStyle: RepStyle, weights: JointWeights) {
    this.name = name
    this.keyposes = keyposes
    this.repStyle = repStyle
    this.weights = weights
    this.requiredLandmarks = new Set()
    for (const jointKey of Object.keys(weights)) {
      if (!weights[jointKey]) {
        continue
      }

      const joint = Joints[jointKey]
      this.requiredLandmarks.add(joint[0])
      this.requiredLandmarks.add(joint[1])
      this.requiredLandmarks.add(joint[2])
    }
  }

  getMissingLandmarks (pose: Pose, threshold: number): LandmarkIndex[] {
    const missingLandmarks = []
    for (let i = 0; i < NUM_LANDMARKS; i++) {
      if (pose.conf(i) < threshold && this.requiredLandmarks.has(i)) {
        missingLandmarks.push(i)
      }
    }

    return missingLandmarks
  }

  isDualSided (): boolean {
    return this.repStyle !== RepStyle.Single
  }

  getScore (pose: Pose, idx: number): number {
    if (idx < 0) {
      idx = this.keyposes.length + idx
    }
    const keypose = this.keyposes[idx]
    if (!keypose) {
      return 0
    }

    return pose.calcPoseScore(keypose, this.weights)
  }

  getScores (pose: Pose): number[] {
    return this.keyposes.map(kp => pose.calcPoseScore(kp, this.weights))
  }

  /**
   * Gets the current exercise state by comparing the input pose against keyposes.
   * @param pose - The pose to evaluate
   * @param threshold - Score threshold for considering a pose valid
   * @returns Exercise state object containing:
   *   - index: Index of best matching keypose (-1 if no keyposes)
   *   - score: Similarity score with best matching keypose (0-1)
   *   - valid: Whether score exceeds threshold
   */
  getState (pose: Pose): ExerciseState {
    if (!this.keyposes.length) {
      return {
        index: -1,
        score: 0,
        valid: false,
      }
    }
    const scores = this.getScores(pose)
    const max = scores.length ? Math.max(...scores) : 0

    // TODO: it should be important that for dualSided exercises the max poses scores are
    // far apart from each other otherwise the transition might be max left -> max right
    return {
      index: scores.indexOf(max),
      score: max,
      valid: true,
    }
  }
}

